import { useEffect, useMemo, useState, type ComponentProps, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ProductSort, ProductStatus, ProductSummaryDto } from '@ecom/contracts';
import {
  EMPTY_PRODUCT_FILTERS,
  useCatalogStore,
  type BulkProductOutcome,
  type ProductFilters,
} from '@/stores/catalog.store';
import { useWorkspaceStore } from '@/stores/workspace.store';
import { useAuthStore } from '@/stores/auth.store';
import { can } from '@/lib/session';
import { AsyncSelect } from '@/components/async-select';
import { categoryOptions } from '@/lib/options';
import { FilterBar, FilterField } from '@/components/filters';
import { Menu, type MenuItem } from '@/components/menu';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, money } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import {
  PRODUCT_SORTS,
  PRODUCT_STATUS_TONE,
  averageStars,
  bulkProductMoves,
  needingMove,
  starsLabel,
  storefrontProductUrl,
} from '@/lib/product-list';
import { ArchiveIcon, BoxIcon, CopyIcon, ExternalIcon, PlusIcon, StarIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, Table, TableScroll, Tabs, Td, Th, Thumb, Tr } from '@/components/page';
import {
  Alert,
  Button,
  ButtonLink,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Input,
  Pill,
  Select,
  TableSkeleton,
} from '@/components/ui';

/** The views over the table: every status, or one — the status filter, one click away. */
type ProductView = 'all' | ProductStatus;
const PRODUCT_VIEWS: ProductView[] = ['all', 'ACTIVE', 'DRAFT', 'ARCHIVED'];

/** Colour chips a row shows before it says "+3"; the product page has the rest. */
const MAX_SWATCHES = 5;

/**
 * The product catalogue.
 *
 * Shows every status, unlike the storefront: draft and archived products are
 * exactly what this screen exists to manage. The status tabs over the table
 * drive the same server-side filter as the status field in the filters, so the
 * two can never disagree.
 *
 * Rows can be selected and moved together — published, sent back to draft or
 * archived — the way the orders list moves orders (ADR-059).
 */
export function ProductsListPage() {
  const user = useAuthStore((state) => state.user);
  const {
    items,
    loading,
    loadingMore,
    error,
    nextCursor,
    filters,
    total,
    saving,
    updating,
    load,
    loadMore,
    setStatusMany,
    duplicate,
  } = useCatalogStore();
  const storefrontUrl = useWorkspaceStore((state) => state.workspace?.storefrontUrl ?? null);
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const navigate = useNavigate();

  // The picker hands back the option it chose, so the chip can name the
  // category without the page holding every category to look one up.
  const [categoryLabel, setCategoryLabel] = useState('');

  useEffect(() => {
    void load();
  }, [load, locale]);

  const hasFilters = Boolean(
    filters.search || filters.status || filters.categoryId || filters.stock || filters.minPrice || filters.maxPrice,
  );
  // Clearing the filters keeps the order someone chose: it is not a filter.
  const clearFilters = () => {
    setCategoryLabel('');
    void load({ ...EMPTY_PRODUCT_FILTERS, sort: filters.sort });
  };

  const chips = [
    ...(filters.categoryId
      ? [
          {
            key: 'category',
            label: categoryLabel || t('category'),
            onRemove: () => {
              setCategoryLabel('');
              void load({ categoryId: '' });
            },
          },
        ]
      : []),
    ...(filters.status
      ? [
          {
            key: 'status',
            label: t(`status${filters.status}` as 'statusDRAFT'),
            onRemove: () => void load({ status: '' }),
          },
        ]
      : []),
    ...(filters.stock
      ? [
          {
            key: 'stock',
            label: filters.stock === 'in' ? t('filterInStock') : t('filterOutOfStock'),
            onRemove: () => void load({ stock: '' }),
          },
        ]
      : []),
    ...(filters.minPrice
      ? [
          {
            key: 'min',
            label: `${t('filterMinPrice')}: ${filters.minPrice}`,
            onRemove: () => void load({ minPrice: '' }),
          },
        ]
      : []),
    ...(filters.maxPrice
      ? [
          {
            key: 'max',
            label: `${t('filterMaxPrice')}: ${filters.maxPrice}`,
            onRemove: () => void load({ maxPrice: '' }),
          },
        ]
      : []),
  ];

  // ─── Selection ─────────────────────────────────────────────────────────────
  // Held here, not in the store: it is what this screen is pointing at, and a
  // new filter or order starts it again.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [outcome, setOutcome] = useState<BulkProductOutcome | null>(null);
  // Archiving asks first — from the bar, or from one row's menu.
  const [archiving, setArchiving] = useState<{ ids: string[]; fromSelection: boolean } | null>(null);

  useEffect(() => setSelected(new Set()), [filters]);

  const chosen = useMemo(() => items.filter((product) => selected.has(product.id)), [items, selected]);
  const moves = bulkProductMoves(chosen);
  const allShown = items.length > 0 && items.every((product) => selected.has(product.id));

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  async function move(ids: string[], status: ProductStatus, fromSelection: boolean) {
    const result = await setStatusMany(ids, status);
    setOutcome(result);
    // What was refused stays selected, ready for another try once fixed.
    if (fromSelection) setSelected(new Set(result.failed.map((failure) => failure.id)));
  }

  const startMove = (status: ProductStatus) => {
    const ids = needingMove(chosen, status);
    if (status === 'ARCHIVED') setArchiving({ ids, fromSelection: true });
    else void move(ids, status, true);
  };

  const rowActions = (product: ProductSummaryDto): MenuItem[] => {
    const storeUrl = storefrontProductUrl(storefrontUrl, product);
    return [
      ...(storeUrl
        ? [
            {
              label: t('productsViewOnStore'),
              icon: <ExternalIcon className="size-4" />,
              onSelect: () => window.open(storeUrl, '_blank', 'noopener,noreferrer'),
            },
          ]
        : []),
      {
        label: t('duplicate'),
        icon: <CopyIcon className="size-4" />,
        disabled: saving,
        // The copy is a draft, opened for editing — the same as the product page's button.
        onSelect: () =>
          void duplicate(product.id).then((copy) => {
            if (copy) navigate(`/products/${copy.id}`);
          }),
      },
      ...(product.status !== 'ARCHIVED'
        ? [
            {
              label: t('archive'),
              icon: <ArchiveIcon className="size-4" />,
              danger: true,
              disabled: updating,
              onSelect: () => setArchiving({ ids: [product.id], fromSelection: false }),
            },
          ]
        : []),
    ];
  };

  const newProduct = (
    <div className="flex items-center gap-2">
      {/* Many at once, like a spreadsheet (ADR-090) — for whoever may change products. */}
      {can(user, 'products:manage') ? (
        <ButtonLink to="/products/grid" variant="secondary">
          {t('productGrid')}
        </ButtonLink>
      ) : null}
      <ButtonLink to="/products/new">
        <PlusIcon className="size-4" />
        {t('newProduct')}
      </ButtonLink>
    </div>
  );

  return (
    <Page title={t('products')} subtitle={t('productsSubtitle')} actions={newProduct} fill>
      {outcome ? (
        <Alert tone={outcome.failed.length > 0 ? 'error' : 'success'}>
          <span className="block">
            {outcome.failed.length > 0
              ? t('bulkResult', { done: count(outcome.done, locale), failed: count(outcome.failed.length, locale) })
              : t('productsBulkDone', { done: count(outcome.done, locale) })}
          </span>
          {outcome.failed.map((failure) => (
            <span key={failure.id} className="block text-xs">
              {failure.name}: {failure.message}
            </span>
          ))}
        </Alert>
      ) : null}

      <ListCard>
        <Tabs
          className="px-2"
          label={t('productStatus')}
          value={filters.status || 'all'}
          onChange={(view) => void load({ status: view === 'all' ? '' : view })}
          items={PRODUCT_VIEWS.map((view) => ({
            id: view,
            label: view === 'all' ? t('productsViewAll') : t(`status${view}` as 'statusACTIVE'),
          }))}
        />
        <ListToolbar>
          <div className="flex flex-wrap items-start gap-3">
            <div className="min-w-0 flex-1">
              <FilterBar
                search={filters.search}
                onSearch={(search) => void load({ search })}
                placeholder={t('searchProducts')}
                total={total ?? undefined}
                shown={items.length}
                chips={chips}
                onClearAll={clearFilters}
              >
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <FilterField label={t('category')} htmlFor="f-category">
                    <AsyncSelect
                      id="f-category"
                      value={filters.categoryId}
                      emptyLabel={t('allCategories')}
                      fetchPage={categoryOptions.page}
                      fetchOne={categoryOptions.one}
                      onChange={(id, option) => {
                        setCategoryLabel(option?.label ?? '');
                        void load({ categoryId: id });
                      }}
                    />
                  </FilterField>

                  <FilterField label={t('productStatus')} htmlFor="f-status">
                    <Select
                      id="f-status"
                      value={filters.status}
                      onChange={(event) => void load({ status: event.target.value as ProductFilters['status'] })}
                    >
                      <option value="">{t('anyValue')}</option>
                      <option value="DRAFT">{t('statusDRAFT')}</option>
                      <option value="ACTIVE">{t('statusACTIVE')}</option>
                      <option value="ARCHIVED">{t('statusARCHIVED')}</option>
                    </Select>
                  </FilterField>

                  <FilterField label={t('filterStock')} htmlFor="f-stock">
                    <Select
                      id="f-stock"
                      value={filters.stock}
                      onChange={(event) => void load({ stock: event.target.value as ProductFilters['stock'] })}
                    >
                      <option value="">{t('anyValue')}</option>
                      <option value="in">{t('filterInStock')}</option>
                      <option value="out">{t('filterOutOfStock')}</option>
                    </Select>
                  </FilterField>

                  <div className="grid grid-cols-2 gap-2">
                    <FilterField label={t('filterMinPrice')} htmlFor="f-minprice">
                      <PausedInput
                        id="f-minprice"
                        dir="ltr"
                        inputMode="decimal"
                        value={filters.minPrice}
                        onCommit={(minPrice) => void load({ minPrice })}
                      />
                    </FilterField>
                    <FilterField label={t('filterMaxPrice')} htmlFor="f-maxprice">
                      <PausedInput
                        id="f-maxprice"
                        dir="ltr"
                        inputMode="decimal"
                        value={filters.maxPrice}
                        onCommit={(maxPrice) => void load({ maxPrice })}
                      />
                    </FilterField>
                  </div>
                </div>
              </FilterBar>
            </div>

            <Select
              aria-label={t('productsSort')}
              title={t('productsSort')}
              value={filters.sort || 'newest'}
              onChange={(event) => void load({ sort: event.target.value as ProductSort })}
              className="w-auto min-w-44"
            >
              {PRODUCT_SORTS.map((sort) => (
                <option key={sort} value={sort}>
                  {t(`productSort_${sort}` as 'productSort_newest')}
                </option>
              ))}
            </Select>
          </div>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading ? (
          <TableSkeleton columns={9} />
        ) : items.length === 0 ? (
          hasFilters ? (
            <EmptyState
              title={t('noProducts')}
              body={t('productsNoMatch')}
              icon={<BoxIcon />}
              action={
                <Button variant="secondary" onClick={clearFilters}>
                  {t('clearFilters')}
                </Button>
              }
            />
          ) : (
            <EmptyState title={t('noProducts')} body={t('noProductsHint')} icon={<BoxIcon />} action={newProduct} />
          )
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th className="w-10">
                    <Checkbox
                      label={t('productsSelectAll')}
                      checked={allShown}
                      onChange={() => setSelected(allShown ? new Set() : new Set(items.map((product) => product.id)))}
                    />
                  </Th>
                  <Th>{t('products')}</Th>
                  <Th>{t('productStatus')}</Th>
                  <Th>{t('colours')}</Th>
                  <Th align="end">{t('variants')}</Th>
                  <Th>{t('stock')}</Th>
                  <Th>{t('rating')}</Th>
                  <Th align="end">{t('price')}</Th>
                  <Th className="w-12">
                    <span className="sr-only">{t('moreActions')}</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {items.map((product) => (
                  // The whole row opens the product; the name stays a real link
                  // for a middle-click or a copied address.
                  <Tr
                    key={product.id}
                    onClick={() => navigate(`/products/${product.id}`)}
                    selected={selected.has(product.id)}
                  >
                    <Td onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        label={t('productsSelectOne', { name: product.name })}
                        checked={selected.has(product.id)}
                        onChange={() => toggle(product.id)}
                      />
                    </Td>
                    <Td>
                      <span className="flex items-center gap-3">
                        <Thumb src={mediaUrl(product.imageUrl)} size="sm" />
                        <span className="min-w-0">
                          <Link
                            to={`/products/${product.id}`}
                            onClick={(event) => event.stopPropagation()}
                            className="block truncate font-medium text-ink hover:text-brand-700"
                          >
                            {product.name}
                          </Link>
                          {/* The slug is an identifier: Latin digits, left to right. */}
                          <span dir="ltr" className="block truncate text-xs text-ink-muted">
                            {product.slug}
                          </span>
                        </span>
                      </span>
                    </Td>
                    <Td>
                      <Pill tone={PRODUCT_STATUS_TONE[product.status] ?? 'neutral'}>
                        {t(`status${product.status}` as 'statusACTIVE')}
                      </Pill>
                    </Td>
                    <Td>
                      <Swatches swatches={product.swatches} />
                    </Td>
                    <Td align="end" data-numeric className="text-ink-muted">
                      {count(product.variantCount, locale)}
                    </Td>
                    <Td>
                      {/* Selling on pre-order reads as such, not as in stock (ADR-079). */}
                      <Pill tone={product.preorderShipsOn ? 'info' : product.inStock ? 'neutral' : 'danger'}>
                        {product.preorderShipsOn ? t('preorderBadge') : product.inStock ? t('inStock') : t('outOfStock')}
                      </Pill>
                    </Td>
                    <Td>
                      <Rating ratingAvg={product.ratingAvg} ratingCount={product.ratingCount} />
                    </Td>
                    <Td align="end" data-numeric className="font-medium text-ink">
                      {money(product.price, locale, product.currency)}
                    </Td>
                    <Td onClick={(event) => event.stopPropagation()}>
                      <Menu label={`${t('moreActions')} — ${product.name}`} items={rowActions(product)} />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
      </ListCard>

      {selected.size > 0 ? (
        // The selection's own toolbar, floating over the list rather than
        // pushing it down — a row that moves under the pointer gets the next
        // click meant for its neighbour. Inverted, so it reads in either theme.
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
          <div
            role="toolbar"
            aria-label={t('selectedCount', { count: count(selected.size, locale) })}
            className="pointer-events-auto flex max-w-full flex-wrap items-center gap-1.5 rounded-full bg-ink px-3 py-2 text-surface shadow-float"
          >
            <span className="px-2 text-sm font-medium">{t('selectedCount', { count: count(selected.size, locale) })}</span>
            <span className="h-4 w-px bg-surface/25" aria-hidden />
            {moves.map((status) => (
              <BarButton key={status} onClick={() => startMove(status)} disabled={updating}>
                {t(`productMove_${status}` as 'productMove_ACTIVE')}
              </BarButton>
            ))}
            <BarButton onClick={() => setSelected(new Set())}>{t('clearSelection')}</BarButton>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={archiving !== null}
        title={
          archiving && archiving.ids.length > 1
            ? t('productsBulkArchiveTitle', { count: count(archiving.ids.length, locale) })
            : t('archiveTitle')
        }
        body={archiving && archiving.ids.length > 1 ? t('productsBulkArchiveBody') : t('archiveBody')}
        confirmLabel={t('archive')}
        cancelLabel={t('cancel')}
        busy={updating}
        onConfirm={async () => {
          if (archiving) await move(archiving.ids, 'ARCHIVED', archiving.fromSelection);
          setArchiving(null);
        }}
        onCancel={() => setArchiving(null)}
      />
    </Page>
  );
}

/**
 * A filter typed digit by digit — a price — applied once typing pauses, not on
 * every keystroke: "150" was three reloads of the list, two of them for prices
 * nobody meant.
 */
function PausedInput({
  value,
  onCommit,
  ...props
}: Omit<ComponentProps<typeof Input>, 'value' | 'onChange'> & { value: string; onCommit: (value: string) => void }) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (draft === value) return undefined;
    const timer = setTimeout(() => onCommit(draft), 300);
    return () => clearTimeout(timer);
  }, [draft, value, onCommit]);

  // A chip removed or "clear all" has to reach the box too.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  return <Input {...props} value={draft} onChange={(event) => setDraft(event.target.value)} />;
}

/** Stars from approved reviews, with how many; a dash, not a zero, before the first. */
function Rating({ ratingAvg, ratingCount }: { ratingAvg: number; ratingCount: number }) {
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const stars = averageStars(ratingAvg, ratingCount);

  if (stars === null) {
    return (
      <span className="text-ink-faint" title={t('productNotRated')}>
        —
      </span>
    );
  }

  const label = starsLabel(stars, locale);
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap"
      title={t('productRatingTitle', { stars: label, count: count(ratingCount, locale) })}
    >
      <StarIcon className="size-3.5 text-warning" />
      <span data-numeric className="text-ink">
        {label}
      </span>
      <span data-numeric className="text-xs text-ink-muted">
        ({count(ratingCount, locale)})
      </span>
    </span>
  );
}

/** The product's colours as chips, named for a screen reader and on hover. */
function Swatches({ swatches }: { swatches: ProductSummaryDto['swatches'] }) {
  const locale = useUiStore((state) => state.locale);

  if (swatches.length === 0) return <span className="text-ink-faint">—</span>;

  const hidden = swatches.length - MAX_SWATCHES;

  return (
    <span className="flex items-center gap-1">
      {swatches.slice(0, MAX_SWATCHES).map((swatch) => (
        <span
          key={swatch.value}
          title={swatch.label}
          className="size-4 shrink-0 rounded-full border border-line"
          style={{ backgroundColor: swatch.hex }}
        >
          <span className="sr-only">{swatch.label}</span>
        </span>
      ))}
      {hidden > 0 ? (
        <span data-numeric className="ms-0.5 text-xs text-ink-muted">
          +{count(hidden, locale)}
        </span>
      ) : null}
    </span>
  );
}

/** A button on the floating selection bar — light on the inverted bar, in either theme. */
function BarButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="min-h-8 rounded-full px-3 text-xs font-medium text-surface transition-colors hover:bg-surface/15 disabled:opacity-50"
    >
      {children}
    </button>
  );
}
